import React from "react";

/**
 * One component, three places: the driver's load card, the company's load
 * modal, and the "what drivers will see" panel on the Post-a-Load review
 * step. Because all three render the same payload shape with the same code,
 * what a company confirms is exactly what a driver reads.
 *
 * Expects the LoadBoardSerializer shape (pay, pay_sub, pay_type, mileage_rate,
 * hourly_rate, min_daily_pay, height_pole_pay, escort_addons, detention_rate,
 * deadhead_rate, overnight, overnight_rate, fuel_surcharge, payment_method,
 * payment_terms, est_total, distance_miles, pay_breakdown{lines,assumptions,vehicles}).
 */

export const usd = (v, digits) => {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(/[$,]/g, ""));
  if (Number.isNaN(n)) return String(v);
  const d = digits ?? (Number.isInteger(n) ? 0 : 2);
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};

/** Headline pay for list cards. Never blank, never "$NaN". */
export function payHeadline(load) {
  const main = load?.pay && String(load.pay).trim() ? String(load.pay) : "Contact dispatcher";
  return { main, sub: load?.pay_sub || "" };
}

function Row({ label, value, strong }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-[13px] text-slate-500 font-medium">{label}</span>
      <span className={`text-[13px] text-right ${strong ? "font-extrabold text-slate-900" : "font-bold text-slate-800"}`}>{value}</span>
    </div>
  );
}

export default function LoadPayBreakdown({ load, variant = "driver" }) {
  if (!load) return null;
  const { main, sub } = payHeadline(load);
  const bd = load.pay_breakdown || {};
  const lines = Array.isArray(bd.lines) ? bd.lines : [];
  const miles = load.distance_miles;

  const overnight = load.overnight
    ? `${usd(load.overnight_rate || 125)} per night`
    : "Not included";
  const payment = [load.payment_method, load.payment_terms].filter(Boolean).join(" · ") || "Per rate confirmation";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="load-pay-breakdown">
      {/* Headline */}
      <div className="px-4 sm:px-5 py-4 bg-emerald-50/70 border-b border-emerald-100 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            {variant === "company" ? "Driver pay as posted" : "What you'll be paid"}
          </p>
          <p className="text-2xl font-black text-emerald-700 leading-tight truncate">{main}</p>
          {sub ? <p className="text-xs font-semibold text-emerald-700/80 mt-0.5">{sub}</p> : null}
        </div>
        {load.pay_type ? (
          <span className="shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800">
            {load.pay_type}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 px-4 sm:px-5 py-2">
        {/* Rate sheet */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pt-3 pb-1">Rate sheet</p>
          <Row label="Per-mile rate" value={load.mileage_rate ? `${usd(load.mileage_rate, 2)}/mi` : null} />
          <Row label="Hourly rate" value={load.hourly_rate ? `${usd(load.hourly_rate)}/hr` : null} />
          <Row label="Minimum per vehicle" value={usd(load.min_daily_pay)} />
          <Row label="High-pole premium" value={load.height_pole_pay ? `+${usd(load.height_pole_pay)} per pole vehicle` : null} />
          <Row label="Other add-ons" value={usd(load.escort_addons)} />
          <Row label="Detention / waiting" value={load.detention_rate ? `${usd(load.detention_rate)}/hr` : null} />
          <Row label="Deadhead (empty travel)" value={load.deadhead_rate ? `${usd(load.deadhead_rate, 2)}/mi` : null} />
          <Row label="Overnight" value={overnight} />
          <Row label="Fuel surcharge" value={load.fuel_surcharge || "Not specified"} />
          <Row label="Payment" value={payment} />
        </div>

        {/* Estimate */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pt-3 pb-1">
            Estimated earnings{bd.vehicles > 1 ? ` · ${bd.vehicles} escort vehicles` : ""}
          </p>
          {lines.length ? (
            <>
              {lines.map((l, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-100">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-slate-700 truncate">{l.label}</p>
                    {l.unit !== "job" && l.unit !== "flat" ? (
                      <p className="text-[11px] text-slate-400">
                        {l.qty} {l.unit} × {usd(l.rate, 2)}{l.note ? ` · ${l.note}` : ""}
                      </p>
                    ) : null}
                  </div>
                  <span className="text-[13px] font-bold text-slate-800 shrink-0">{usd(l.amount, 2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-extrabold text-slate-900">Estimated total</span>
                <span className="text-lg font-black text-emerald-700">{usd(load.est_total, 2)}</span>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-400 pb-3">
                {miles ? `Based on ~${Math.round(miles)} route miles. ` : ""}
                {Array.isArray(bd.assumptions) && bd.assumptions.length ? bd.assumptions.join(" ") + " " : ""}
                Estimate only — final pay follows the signed rate confirmation.
              </p>
            </>
          ) : (
            <p className="text-[13px] text-slate-500 py-3 leading-relaxed">
              The company hasn't posted a calculable rate for this load. Confirm the rate with the
              dispatcher before accepting.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
